import { selectAPI } from '@api/API.sagas';
import {
    API_GET_CUSTOMER_STATE_EDIT,
    API_GET_DOCUMENT_TO_SIGN,
    API_GET_DOCUMENT_TO_SIGN_FAIL,
    API_GET_DOCUMENT_TO_SIGN_SUCCESS,
    API_GET_FUNDING_REQUIREMENT,
    API_GET_FUNDING_REQUIREMENT_SUCCESS,
    API_GET_LOAN_DOCUMENTS,
    API_GET_LOAN_DOCUMENTS_SUCCESS,
    API_GET_SIGNED_DOCUMENT,
    API_GET_SIGNED_DOCUMENT_SUCCESS,
    API_GET_URL_FILE_TO_DOWNLOAD,
    API_GET_URL_FILE_TO_DOWNLOAD_SUCCESS,
    API_PUT_AGREE_CUSTOMER_DOCUMENT,
    API_PUT_AGREE_CUSTOMER_DOCUMENT_FAIL,
    API_PUT_AGREE_CUSTOMER_DOCUMENT_SUCCESS,
    API_SAVE_CUSTOMER_STATE,
    API_SAVE_CUSTOMER_STATE_SUCCESS
} from '@api/lendingAPI/LendingAPI.actions';
import { CLOSE_MODAL, OPEN_MODAL } from '@iwModals/Modals.actions';
import { mergeStates } from '@iwProvider/StoreHelper';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { delay } from 'redux-saga';
import { all, fork, put, take, takeLatest } from 'redux-saga/effects';

import {
    DRAW_DOWN_SIGN_DOCUMENTS_DATA,
    SIGN_DOCUMENT_AGREE_AND_CONTINUE_ACTION,
    SIGN_DOCUMENT_AGREE_DONE,
    SIGN_DOCUMENT_AGREE_PENDING,
    SIGN_DOCUMENT_AGREE_ERROR,
    SIGN_DOCUMENT_DROP_AGREE,
    SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_SHOW_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_SHOW_DOCUMENT_ERROR,
    SIGN_DOCUMENT_SHOW_SIGNED_DOCUMENT_CONTENT
} from './signDocumentAction/SignDocuments.actions';
import {
    buildBodyAgreeDocument,
    getCustomerDocumentArray,
    getSignedDocument,
    getSigningDocument
} from './signDocumentAction/SignDocumentsHelper';
import { MAJOR_SHAREHOLDER_ADDED } from '../../drawDown/DrawDown.actions';
import {
    ADD_PAYMENT_METHOD,
    BANK_ACCOUNT_FUNDING_REQUIREMENT_COMPLETED,
    DEBIT_CARD_FUNDING_REQUIREMENT_COMPLETED,
    REGISTER_BANK_ACCOUNT_SUCCESS,
    REGISTER_DEBIT_CARD_SUCCESS,
    RESET_PAYMENT_METHOD
} from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';
import { ACTIVATE_ID_CHECK_VERIFICATION } from '../../modals/idCheckVerificationModal/IdCheckVerificationModal.actions';
import { getProductTypeFromOfferId } from '../../multiLoanDashboard/accountsSelector';
import { FUNDING_STEP_ACTION } from '../Funding.actions';

export const fundingStepsSagaWorkers = [
    fork(FundingStepActionWorker),
    fork(handleMajorShareholderWorker),
    fork(updateDrawDownStateWorker)
];

function* FundingStepActionWorker() {
    yield takeLatest(FUNDING_STEP_ACTION, function* (action) {
        switch (action.step) {
            case 'DEBIT_CARD':
                yield handleLinkDebitCardWorker();
                break;
            case 'BANK_DETAILS':
                yield handleBankAccountCardWorker({
                    isInNewDrawdownFlow: true
                });
                break;
            case 'ONFIDO_VERIFICATION':
                yield handleOnfidoVerification();
                break;
            case 'SIGN_DOCUMENT':
                yield completeSignDocumentActionWorker({
                    signDocumentAction: action.signDocumentAction
                });
                break;
            case 'SIGN_DOCUMENT_WET':
                yield getWetSignDocumentWorker({
                    signDocumentAction: action.signDocumentAction
                });
                break;
            case 'VIEW_SIGN_DOCUMENT':
                yield viewSignDocumentActionWorker({
                    signDocumentAction: action.signDocumentAction
                });
        }
    });
}

function* handleMajorShareholderWorker() {
    yield takeLatest(
        MAJOR_SHAREHOLDER_ADDED,
        function* ({ onUpdatedFundingRequirements }) {
            yield* updateFundingRequirements();
            const { funding_requirement } = yield selectAPI();
            onUpdatedFundingRequirements(funding_requirement);
        }
    );
}

function* handleOnfidoVerification() {
    yield put({ type: ACTIVATE_ID_CHECK_VERIFICATION });
}

function* handleLinkDebitCardWorker() {
    yield put({
        type: ADD_PAYMENT_METHOD,
        paymentMethodType: 'DEBIT_CARD'
        // TODO: Change this to  'PRIMARY_DEBIT_CARD' and remove dropdown from debit card in draw down step 2
    });
    const nextAction = yield take([CLOSE_MODAL, REGISTER_DEBIT_CARD_SUCCESS]);

    switch (nextAction.type) {
        case CLOSE_MODAL:
            yield put({ type: RESET_PAYMENT_METHOD });
            break;
        case REGISTER_DEBIT_CARD_SUCCESS:
            yield* updateFundingRequirements({
                type: DEBIT_CARD_FUNDING_REQUIREMENT_COMPLETED
            });

            yield delay(800);

            yield put({ type: RESET_PAYMENT_METHOD });

            break;
    }
}

function* handleBankAccountCardWorker(action) {
    yield put({
        type: ADD_PAYMENT_METHOD,
        paymentMethodType: 'BANK_ACCOUNT'
    });

    const nextAction = yield take([CLOSE_MODAL, REGISTER_BANK_ACCOUNT_SUCCESS]);

    switch (nextAction.type) {
        case CLOSE_MODAL:
            yield put({ type: RESET_PAYMENT_METHOD });
            break;
        case REGISTER_BANK_ACCOUNT_SUCCESS:
            yield delay(2000);

            yield* updateFundingRequirements(
                {
                    type: BANK_ACCOUNT_FUNDING_REQUIREMENT_COMPLETED
                },
                //isInNewDrawdownFlow is used to skip sidebar state updates
                action ? action.isInNewDrawdownFlow : false
            );

            yield delay(800);

            yield put({ type: RESET_PAYMENT_METHOD });

            break;
    }
}

export function* getFundingRequirements() {
    const { formal_offers } = yield selectAPI();
    const location = getCurrentLocation();

    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');
    const productType = getProductTypeFromOfferId(formal_offers, offerId);

    yield put({
        type: API_GET_FUNDING_REQUIREMENT,
        amount,
        offerId,
        productType
    });
}

function* updateFundingRequirements(onCompleteAction) {
    yield* getFundingRequirements();
    yield take(API_GET_FUNDING_REQUIREMENT_SUCCESS);
    if (onCompleteAction) {
        yield put(onCompleteAction);
    }
    yield put({ type: CLOSE_MODAL });
}

function* getWetSignDocumentWorker(action) {
    const { customer_state: initialCustomerState } = yield selectAPI();
    // TODO the function should get the whole customer state, put this get in the helper function
    const documentsInUI =
        initialCustomerState?.ui?.draw_down?.credit_facility_customer_document;

    yield put({
        type: API_GET_URL_FILE_TO_DOWNLOAD,
        url: action.signDocumentAction.url,
        uid: action.signDocumentAction.uid
    });

    yield take(API_GET_URL_FILE_TO_DOWNLOAD_SUCCESS);

    const documentDownloadsStatus = getCustomerDocumentArray(
        documentsInUI,
        action
    );

    yield put({
        type: 'api/UPDATE_DRAW_DOWN_STATE',
        loan_documents: documentDownloadsStatus
    });

    yield take(API_SAVE_CUSTOMER_STATE_SUCCESS);

    const {
        customer_state: updatedCustomerState,
        funding_requirement,
        loan_documents
    } = yield selectAPI();

    // TODO the event should use only the whole customer state
    yield put({
        type: DRAW_DOWN_SIGN_DOCUMENTS_DATA,
        customer_state: updatedCustomerState,
        loan_documents,
        funding_requirement
    });
}

function* completeSignDocumentActionWorker(action) {
    const { loan_documents } = yield selectAPI();
    const signingDocument = getSigningDocument(
        loan_documents,
        action.signDocumentAction.uid
    );
    yield put({ type: SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT, signingDocument });

    yield put({ type: OPEN_MODAL, id: 'SignDocumentsActionModal' });
    yield put({
        type: API_GET_DOCUMENT_TO_SIGN,
        url: signingDocument.unsignedURL,
        uid: signingDocument.uid
    });

    const actionOutput = yield take([
        API_GET_DOCUMENT_TO_SIGN_SUCCESS,
        API_GET_DOCUMENT_TO_SIGN_FAIL
    ]);

    switch (actionOutput.type) {
        case API_GET_DOCUMENT_TO_SIGN_SUCCESS:
            yield put({
                type: SIGN_DOCUMENT_SHOW_DOCUMENT_CONTENT,
                signingDocumentContent: actionOutput.response.data,
                documentType: signingDocument.documentType
            });
            break;
        case API_GET_DOCUMENT_TO_SIGN_FAIL:
            yield put({ type: SIGN_DOCUMENT_SHOW_DOCUMENT_ERROR });
            break;
    }

    const nextAction = yield take([
        SIGN_DOCUMENT_AGREE_AND_CONTINUE_ACTION,
        CLOSE_MODAL
    ]);

    switch (nextAction.type) {
        case SIGN_DOCUMENT_AGREE_AND_CONTINUE_ACTION:
            yield agreeSignDocument(signingDocument);
            yield updateSignDocumentStatusWorker();
            yield put({ type: CLOSE_MODAL });
            break;
        case CLOSE_MODAL:
            yield put({ type: SIGN_DOCUMENT_DROP_AGREE });
            break;
    }

    yield updateSignDocumentStatusWorker();
}

function* viewSignDocumentActionWorker(action) {
    const { loan_documents } = yield selectAPI();

    const signedDocument = getSignedDocument(
        loan_documents,
        action.signDocumentAction.uid
    );

    yield put({
        type: SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT,
        signedDocument
    });

    all([
        yield put({ type: OPEN_MODAL, id: 'ViewSignedDocumentModal' }),
        yield put({
            type: API_GET_SIGNED_DOCUMENT,
            url: signedDocument.signedURL,
            uid: signedDocument.uid
        })
    ]);

    const actionOutput = yield take(API_GET_SIGNED_DOCUMENT_SUCCESS);
    yield put({
        type: SIGN_DOCUMENT_SHOW_SIGNED_DOCUMENT_CONTENT,
        signedDocumentContent: actionOutput.response.data
    });
}

function* updateSignDocumentStatusWorker() {
    const { formal_offers } = yield selectAPI();
    const location = getCurrentLocation();

    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');
    const productType = getProductTypeFromOfferId(formal_offers, offerId);

    yield put({
        type: API_GET_LOAN_DOCUMENTS,
        productType,
        formalOfferId: offerId,
        amount: amount
    });

    yield take(API_GET_LOAN_DOCUMENTS_SUCCESS);
}

function* agreeSignDocument(signingDocument) {
    yield put({ type: SIGN_DOCUMENT_AGREE_PENDING });

    const putDocApiResponse = yield putCustomerDocument(signingDocument);

    if (putDocApiResponse.type === API_PUT_AGREE_CUSTOMER_DOCUMENT_SUCCESS) {
        yield put({ type: SIGN_DOCUMENT_AGREE_DONE });
    } else {
        yield put({ type: SIGN_DOCUMENT_AGREE_ERROR });
    }
}
function* putCustomerDocument(signingDocument) {
    const agreeDate = new Date().toISOString().substring(0, 10);
    const body = buildBodyAgreeDocument(signingDocument.uid, agreeDate);

    yield put({ type: API_PUT_AGREE_CUSTOMER_DOCUMENT, body });

    return yield take([
        API_PUT_AGREE_CUSTOMER_DOCUMENT_SUCCESS,
        API_PUT_AGREE_CUSTOMER_DOCUMENT_FAIL
    ]);
}

function* updateDrawDownStateWorker() {
    yield takeLatest('api/UPDATE_DRAW_DOWN_STATE', function* (action) {
        // TODO: if the data didn't change, probably this should not PUT
        const { customer_state } = yield selectAPI();
        const uiDrawDownState = customer_state?.ui?.draw_down;

        yield put({
            type: API_GET_CUSTOMER_STATE_EDIT,
            data: {
                ui: {
                    draw_down: mergeStates(uiDrawDownState, {
                        step: action.drawDownStep,
                        amount: action.drawDownAmount,
                        funding_requirements: action.funding_requirements,
                        datetime: action.datetime,
                        credit_facility_customer_document:
                            action.loan_documents,
                        wet_sign_docs_marked_sent:
                            action.wetSignDocsMarkedAsSent,
                        penny_transfer_sent: action.pennyTransferSent,
                        sms_verification_messages_sent:
                            action.smsVerificationMessagesSent
                    })
                }
            }
        });

        yield put({ type: API_SAVE_CUSTOMER_STATE });
        yield take(API_SAVE_CUSTOMER_STATE_SUCCESS);
    });
}
